import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowSquareOut, CalendarBlank, ClipboardText, DownloadSimple, Eye, FileText, FloppyDisk, Handshake, LinkSimple, PencilSimple, Plus,
  Trash, UploadSimple, User, UserPlus, Users, Wallet,
} from '@phosphor-icons/react';
import {
  Auswahl, BestaetigenDialog, Chip, FeldRaster, ImmoButton, InnenKarte, IosSwitch, KartenTitel, LivePreviewCard, SearchSelect,
  SegmentedControl, TextFeld, ToastPill, useToastPill, type SucheOption,
} from '../../../../../components/immobilien/ui';
import { supabase } from '../../../../../lib/supabase/client';
import { ladeMieterDokumentHoch, oeffneMieterDokument, MIETER_DOK_SPALTEN as DOK_SPALTEN, type MieterDokument as DokZeile } from '../../../../../lib/mieter/mieterDokumente';
import { bereinigeSuchbegriff } from '../../../../../lib/kontakte/personenSuche';
import { eur, heuteIso, initialen } from '../../../../../lib/immobilien/format';
import { deDatum } from '../../../../../lib/immobilien/objektDetail';
import { useOpenKontakt } from '../../../../../lib/immobilien/openKontakt';
import { kontaktName } from '../../../../../lib/objekte/objektKontakte';
import {
  KAUTIONSARTEN, MIETER_STATUS, MIETINTERVALLE, VERTRAGSARTEN, gesamtAusEntwurf, historieUpdate, leseZahl, neueAenderung, payloadAusEntwurf, startAusMieter,
  type MvEntwurf,
} from '../../../../../lib/immobilien/mietverhaeltnisFormular';
import { aktuellerStand, gesamtmiete, positionLabel, summePositionen, zeitraumLabel, type Mietstand } from '../../../../../lib/mieter/mietstaende';
import type { MietverhaeltnisRoh } from '../../../../../lib/immobilien/useEinheitDetail';
import { useEinheitSeite } from '../EinheitSeitenKontext';
import { MietstandEditor } from './MietstandEditor';

type Reiter = 'mieter' | 'vertrag' | 'miete' | 'einzug';
interface Mitmieter { id: string; vorname: string | null; nachname: string | null; firma_name: string | null; email: string | null; telefon: string | null; mobil: string | null }

const ABSOLUT = /^https?:\/\//i;

/** Auswahl mit vorhandenem, nicht vorgesehenem Wert (alte Daten) als zusätzliche Option. */
function optionen(liste: readonly string[], wert: string) {
  const alle = wert && !liste.includes(wert) ? [...liste, wert] : liste;
  return alle.map(x => ({ key: x, label: x }));
}

function Schalter({ label, hinweis, an, onChange, linie = false }: { label: string; hinweis?: string; an: boolean; onChange: (an: boolean) => void; linie?: boolean }) {
  return (
    <div className="flex items-start justify-between" style={{ gap: 14, padding: '10px 0', borderBottom: linie ? '1px solid var(--line)' : undefined }}>
      <span className="flex flex-col min-w-0" style={{ gap: 2 }}>
        <span style={{ fontSize: 14, fontWeight: 700 }}>{label}</span>
        {hinweis && <span style={{ fontSize: 12, color: 'var(--ink3)', lineHeight: 1.4 }}>{hinweis}</span>}
      </span>
      <IosSwitch an={an} onChange={onChange} label={label} />
    </div>
  );
}

function Textbereich({ label, wert, onChange, platzhalter }: { label: string; wert: string; onChange: (v: string) => void; platzhalter?: string }) {
  return (
    <label className="flex flex-col" style={{ gap: 6 }}>
      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>{label}</span>
      <textarea value={wert} onChange={e => onChange(e.target.value)} rows={3} placeholder={platzhalter}
        style={{ width: '100%', resize: 'vertical', border: 'none', outline: 'none', borderRadius: 15, padding: 14, background: 'var(--bg)', boxShadow: 'inset 0 0 0 1px var(--line)', fontSize: 15, lineHeight: 1.5, color: 'var(--ink)' }} />
    </label>
  );
}

/**
 * „Mietverhältnis bearbeiten“ im neuen Design – ersetzt den bisherigen Mietverhältnis-Dialog
 * mit allen Feldern (Mieter, Vertrag, Miete, Ein-/Auszug). Speichern wie bisher in mieter
 * und entity_links (Mitmieter); Mietstände und Dokumente werden sofort gespeichert.
 */
export function MietverhaeltnisBereich({ mieterId, startReiter, onFertig, registriereVerlassen }: {
  mieterId: string;
  startReiter?: string | null;
  onFertig: () => void;
  /** Escape / „Alle Widgets“ laufen über die Rückfrage bei ungespeicherten Änderungen */
  registriereVerlassen: (f: (() => void) | null) => void;
}) {
  const s = useEinheitSeite();
  const zeile = s.daten.mieter.find(m => m.id === mieterId) ?? null;
  if (!zeile) {
    return (
      <InnenKarte>
        <KartenTitel icon={Handshake}>Mietverhältnis nicht gefunden</KartenTitel>
        <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Das Mietverhältnis gehört nicht (mehr) zu dieser Einheit.</span>
        <ImmoButton onClick={onFertig} style={{ alignSelf: 'flex-start' }}>Zurück</ImmoButton>
      </InnenKarte>
    );
  }
  const reiter: Reiter = startReiter === 'vertrag' || startReiter === 'miete' || startReiter === 'einzug' ? startReiter : 'mieter';
  return <Formular key={zeile.id} m={zeile} startReiter={reiter} onFertig={onFertig} registriereVerlassen={registriereVerlassen} />;
}

function Formular({ m, startReiter, onFertig, registriereVerlassen }: { m: MietverhaeltnisRoh; startReiter: Reiter; onFertig: () => void; registriereVerlassen: (f: (() => void) | null) => void }) {
  const s = useEinheitSeite();
  const navigate = useNavigate();
  const openKontakt = useOpenKontakt();
  const [toast, zeige] = useToastPill();
  const heute = heuteIso();
  const start = useMemo(() => startAusMieter(m as unknown as Record<string, unknown>, heute), [m, heute]);

  const [reiter, setReiter] = useState<Reiter>(startReiter);
  const [e, setE] = useState<MvEntwurf>(start.entwurf);
  const [kautionAn, setKautionAn] = useState(start.kautionAn);
  const [staende, setStaende] = useState<Mietstand[]>(start.staende);
  const [mitmieter, setMitmieter] = useState<Mitmieter[]>([]);
  const [mitmieterStart, setMitmieterStart] = useState<string>('[]');
  const [docs, setDocs] = useState<DokZeile[]>([]);
  const [standEntwurf, setStandEntwurf] = useState<{ stand: Mietstand; istNeu: boolean } | null>(null);
  const [standLoeschen, setStandLoeschen] = useState<Mietstand | null>(null);
  const [dokLoeschen, setDokLoeschen] = useState<DokZeile | null>(null);
  const [speichert, setSpeichert] = useState(false);
  const [laedtHoch, setLaedtHoch] = useState(false);
  const [verlassen, setVerlassen] = useState(false);
  const datei = useRef<HTMLInputElement>(null);
  const set = <K extends keyof MvEntwurf>(k: K) => (v: MvEntwurf[K]) => setE(x => ({ ...x, [k]: v }));

  // Mitmieter und Dokumente wie bisher laden
  useEffect(() => {
    let aktiv = true;
    void (async () => {
      const { data: links } = await supabase.from('entity_links').select('target_id')
        .eq('source_type', 'mieter').eq('source_id', m.id).eq('link_type', 'mitmieter');
      const ids = ((links ?? []) as { target_id: string }[]).map(l => l.target_id);
      const liste = ids.length
        ? (((await supabase.from('mieter').select('id, vorname, nachname, firma_name, email, telefon, mobil').in('id', ids)).data ?? []) as Mitmieter[])
        : [];
      if (!aktiv) return;
      setMitmieter(liste);
      setMitmieterStart(JSON.stringify(liste.map(x => x.id).sort()));
    })();
    void supabase.from('dokumente').select(DOK_SPALTEN).eq('mieter_id', m.id).eq('is_deleted', false).order('created_at', { ascending: false })
      .then(({ data }) => { if (aktiv) setDocs((data ?? []) as DokZeile[]); });
    return () => { aktiv = false; };
  }, [m.id]);

  const geaendert = JSON.stringify(e) !== JSON.stringify(start.entwurf) || kautionAn !== start.kautionAn
    || JSON.stringify(mitmieter.map(x => x.id).sort()) !== mitmieterStart;

  // ── Speichern (wie bisher handleSubmit) ─────────────────────────────────────
  const speichere = async () => {
    setSpeichert(true);
    try {
      const payload = payloadAusEntwurf(e, staende, kautionAn, heute);
      const { error } = await supabase.from('mieter').update(payload).eq('id', m.id);
      if (error) throw new Error(error.message);
      const del = await supabase.from('entity_links').delete().eq('source_type', 'mieter').eq('source_id', m.id).eq('link_type', 'mitmieter');
      if (del.error) throw new Error(`Mitmieter: ${del.error.message}`);
      if (mitmieter.length) {
        const ins = await supabase.from('entity_links').insert(mitmieter.map(x => ({
          source_type: 'mieter', source_id: m.id, target_type: 'mieter', target_id: x.id, link_type: 'mitmieter',
        })));
        if (ins.error) throw new Error(`Mitmieter: ${ins.error.message}`);
      }
      s.geaendert('mieter');
      onFertig();
    } catch (err) {
      zeige(`Nicht gespeichert: ${err instanceof Error ? err.message : 'Unbekannter Fehler'}`, true);
    } finally {
      setSpeichert(false);
    }
  };

  // ── Mietstände: sofort sichern und die vier Felder spiegeln (wie bisher) ─────
  const sichereStaende = async (neu: Mietstand[], meldung: string) => {
    const u = historieUpdate(neu, heute);
    const { error } = await supabase.from('mieter').update({ miet_historie: u.miet_historie, ...(u.spiegel ?? {}) }).eq('id', m.id);
    if (error) { zeige(`Mietstand nicht gespeichert: ${error.message}`, true); return false; }
    setStaende(u.miet_historie);
    if (u.spiegel) {
      const t = (n: number) => String(n).replace('.', ',');
      setE(x => ({ ...x, kaltmiete: t(u.spiegel!.kaltmiete), nebenkosten_vorauszahlung: t(u.spiegel!.nebenkosten_vorauszahlung), heizkosten: t(u.spiegel!.heizkosten), weitere_kosten: t(u.spiegel!.weitere_kosten) }));
    }
    zeige(meldung);
    s.geaendert('mieter');
    return true;
  };

  // ── Mitmieter suchen ─────────────────────────────────────────────────────────
  const sucheMieter = useCallback(async (anfrage: string, _typ: string | null, signal: AbortSignal): Promise<SucheOption[]> => {
    const muster = `%${bereinigeSuchbegriff(anfrage).replace(/%/g, ' ')}%`;
    const { data, error } = await supabase.from('mieter').select('id, vorname, nachname, firma_name, email, telefon, mobil')
      .or(`vorname.ilike.${muster},nachname.ilike.${muster},firma_name.ilike.${muster},email.ilike.${muster}`).limit(8).abortSignal(signal);
    if (error) throw new Error(error.message);
    return ((data ?? []) as Mitmieter[]).filter(x => x.id !== m.id).map(x => ({ id: x.id, label: kontaktName(x), sub: x.email ?? undefined, typ: 'mieter' }));
  }, [m.id]);

  const mitmieterHinzu = async (o: SucheOption) => {
    if (mitmieter.some(x => x.id === o.id)) { zeige(`${o.label} ist bereits Mitmieter`, true); return; }
    const { data } = await supabase.from('mieter').select('id, vorname, nachname, firma_name, email, telefon, mobil').eq('id', o.id).maybeSingle();
    if (data) setMitmieter(liste => [...liste, data as Mitmieter]);
  };

  // ── Dokumente ────────────────────────────────────────────────────────────────
  const oeffneDok = async (d: DokZeile, download: boolean) => {
    if (!(await oeffneMieterDokument(d, download))) zeige('Datei konnte nicht geöffnet werden', true);
  };

  const hochladen = async (f: File) => {
    setLaedtHoch(true);
    try {
      const dok = await ladeMieterDokumentHoch({
        datei: f, mieterId: m.id, mieterName: `${m.vorname ?? ''} ${m.nachname ?? ''}`.trim(), objektId: s.daten.objekt.id, kategorie: 'Mietvertrag', ordner: 'mietvertraege',
      });
      setDocs(liste => [dok, ...liste]);
      zeige('Mietvertrag hochgeladen');
      s.geaendert('dokumente');
    } catch (err) {
      zeige(`Upload fehlgeschlagen: ${err instanceof Error ? err.message : 'Unbekannter Fehler'}`, true);
    } finally {
      setLaedtHoch(false);
      if (datei.current) datei.current.value = '';
    }
  };

  const sucheDokument = useCallback(async (anfrage: string, _typ: string | null, signal: AbortSignal): Promise<SucheOption[]> => {
    const q = bereinigeSuchbegriff(anfrage).replace(/%/g, ' ');
    const { data, error } = await supabase.from('dokumente').select('id, titel, datei_name, kategorie, created_at, mieter_id').eq('is_deleted', false)
      .or(`datei_name.ilike.%${q}%,kategorie.ilike.%${q}%,titel.ilike.%${q}%`).order('created_at', { ascending: false }).limit(10).abortSignal(signal);
    if (error) throw new Error(error.message);
    return ((data ?? []) as DokZeile[]).map(d => ({
      id: d.id, label: d.titel || d.datei_name || 'Dokument',
      sub: [d.kategorie, deDatum(d.created_at), d.mieter_id && d.mieter_id !== m.id ? 'gehört zu anderem Mieter' : d.mieter_id === m.id ? 'bereits verknüpft' : ''].filter(Boolean).join(' · '),
    }));
  }, [m.id]);

  const verknuepfen = async (o: SucheOption) => {
    const { data } = await supabase.from('dokumente').select(DOK_SPALTEN).eq('id', o.id).maybeSingle();
    const d = data as DokZeile | null;
    if (!d) { zeige('Dokument nicht gefunden', true); return; }
    if (d.mieter_id === m.id) { zeige('Dokument ist bereits verknüpft', true); return; }
    if (d.mieter_id) { zeige('Das Dokument gehört zu einem anderen Mieter – bitte im Dokumentenbereich umhängen', true); return; }
    const { error } = await supabase.from('dokumente').update({ mieter_id: m.id }).eq('id', d.id);
    if (error) { zeige(`Nicht verknüpft: ${error.message}`, true); return; }
    setDocs(liste => [{ ...d, mieter_id: m.id }, ...liste]);
    zeige('Dokument verknüpft');
    s.geaendert('dokumente');
  };

  const loescheDok = async () => {
    if (!dokLoeschen) return;
    const pfad = (dokLoeschen.datei_url ?? '').trim();
    if (pfad && !ABSOLUT.test(pfad)) await supabase.storage.from('dokumente').remove([pfad]);
    const { error } = await supabase.from('dokumente').delete().eq('id', dokLoeschen.id);
    if (error) { zeige(`Nicht gelöscht: ${error.message}`, true); return; }
    setDocs(liste => liste.filter(x => x.id !== dokLoeschen.id));
    setDokLoeschen(null);
    zeige('Dokument gelöscht');
    s.geaendert('dokumente');
  };

  // ── Ableitungen ──────────────────────────────────────────────────────────────
  const akt = aktuellerStand(staende, heute);
  const aktPositionen = akt?.positionen ?? [];
  const gesamt = gesamtAusEntwurf(aktPositionen.length ? { ...e, weitere_kosten: String(summePositionen(aktPositionen)) } : e);
  const name = kontaktName({ vorname: e.vorname, nachname: e.nachname, firma_name: e.firma_name });

  const abbrechen = useCallback(() => (geaendert ? setVerlassen(true) : onFertig()), [geaendert, onFertig]);
  useEffect(() => { registriereVerlassen(abbrechen); return () => registriereVerlassen(null); }, [abbrechen, registriereVerlassen]);

  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <div className="flex flex-wrap items-start" style={{ gap: 16 }}>
        <div className="flex flex-col min-w-0" style={{ flex: '2 1 420px', gap: 14 }}>
          <div className="immo-ohne-scrollbar overflow-x-auto">
            <SegmentedControl ariaLabel="Bereiche des Mietverhältnisses" wert={reiter} onChange={setReiter}
              segmente={[{ key: 'mieter', label: 'Mieter', icon: User }, { key: 'vertrag', label: 'Vertrag', icon: FileText }, { key: 'miete', label: 'Miete', icon: Wallet }, { key: 'einzug', label: 'Ein- & Auszug', icon: ClipboardText }]} />
          </div>

          {reiter === 'mieter' && (<>
            <InnenKarte>
              <KartenTitel icon={User} rechts={<button type="button" onClick={() => openKontakt(m.id, 'mieter')} className="flex items-center cursor-pointer"
                style={{ gap: 4, border: 'none', background: 'transparent', padding: 0, fontSize: 12, fontWeight: 700, color: 'var(--acc)' }}>Kontakt öffnen<ArrowSquareOut size={12} /></button>}>
                Hauptmieter
              </KartenTitel>
              <FeldRaster minBreite={170}>
                <TextFeld label="Vorname" wert={e.vorname} onChange={set('vorname')} />
                <TextFeld label="Nachname" wert={e.nachname} onChange={set('nachname')} />
                <TextFeld label="Firma (optional)" wert={e.firma_name} onChange={set('firma_name')} />
                <TextFeld label="E-Mail" type="email" wert={e.email} onChange={set('email')} />
                <TextFeld label="Festnetz" type="tel" wert={e.telefon} onChange={set('telefon')} />
                <TextFeld label="Handynummer" type="tel" wert={e.mobil} onChange={set('mobil')} />
                <TextFeld label="IBAN" wert={e.mieter_iban} onChange={set('mieter_iban')} />
                <TextFeld label="Kontoinhaber" wert={e.mieter_kontoinhaber} onChange={set('mieter_kontoinhaber')} />
              </FeldRaster>
            </InnenKarte>
            <InnenKarte>
              <KartenTitel icon={Users} rechts={mitmieter.length ? `${mitmieter.length}` : undefined}>Weitere Mieter im Mietverhältnis</KartenTitel>
              <SearchSelect label="Bestehenden Kontakt hinzufügen" platzhalter="Name, Firma oder E-Mail …" suche={sucheMieter} onWahl={o => { void mitmieterHinzu(o); }} />
              {mitmieter.length === 0 && <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Noch keine weiteren Mieter.</span>}
              {mitmieter.map(x => (
                <div key={x.id} className="flex items-center flex-wrap" style={{ gap: 12, padding: '8px 0', borderBottom: '1px solid var(--line)' }}>
                  <span className="flex items-center justify-center shrink-0" style={{ width: 38, height: 38, borderRadius: '50%', background: 'var(--fill2)', fontSize: 13, fontWeight: 700 }}>{initialen(kontaktName(x))}</span>
                  <button type="button" onClick={() => openKontakt(x.id, 'mieter')} className="flex flex-col flex-1 min-w-0 text-left cursor-pointer"
                    style={{ border: 'none', background: 'transparent', padding: 0, color: 'var(--ink)', minWidth: 160 }}>
                    <span className="truncate" style={{ fontSize: 14, fontWeight: 700 }}>{kontaktName(x)}</span>
                    <span className="truncate" style={{ fontSize: 12, color: 'var(--ink3)' }}>{[x.email, x.mobil || x.telefon].filter(Boolean).join(' · ') || 'keine Kontaktdaten'}</span>
                  </button>
                  <ImmoButton groesse={32} art="bad" icon={Trash} aria-label={`${kontaktName(x)} entfernen`} title="Aus dem Mietverhältnis entfernen"
                    onClick={() => setMitmieter(liste => liste.filter(y => y.id !== x.id))} />
                </div>
              ))}
              <span className="flex items-center flex-wrap" style={{ gap: 8, fontSize: 12, color: 'var(--ink3)' }}>
                Neuer Kontakt?
                <button type="button" onClick={() => navigate('/admin/kontakte')} className="flex items-center cursor-pointer"
                  style={{ gap: 4, border: 'none', background: 'transparent', padding: 0, fontSize: 12, fontWeight: 700, color: 'var(--acc)' }}>
                  <UserPlus size={13} />Im Kontaktbereich anlegen
                </button>
              </span>
            </InnenKarte>
          </>)}

          {reiter === 'vertrag' && (<>
            <InnenKarte>
              <KartenTitel icon={FileText}>Vertrag</KartenTitel>
              <Auswahl label="Status des Mietverhältnisses" wert={e.status || 'aktiv'} onChange={set('status')}
                optionen={[...MIETER_STATUS, ...(e.status && !MIETER_STATUS.some(x => x.key === e.status) ? [{ key: e.status, label: e.status }] : [])]} />
              {(e.status === 'ehemalig' || e.status === 'inaktiv') && (
                <span style={{ fontSize: 12, color: 'var(--warn)' }}>Nach dem Speichern zählt das Mietverhältnis nicht mehr als laufend – die Einheit gilt dann als frei, wenn kein anderer Mieter wohnt.</span>
              )}
              <Auswahl label="Vertragsart" wert={e.vertragsart || null} onChange={set('vertragsart')} optionen={optionen(VERTRAGSARTEN, e.vertragsart)} />
              <FeldRaster minBreite={170}>
                <TextFeld label="Mietbeginn" type="date" wert={e.mietbeginn} onChange={set('mietbeginn')} />
                <TextFeld label="Vertragsende (optional)" type="date" wert={e.mietende} onChange={set('mietende')} />
                <TextFeld label="Miete fällig am (Tag)" type="number" min={1} max={31} wert={e.miete_faellig_tag} onChange={set('miete_faellig_tag')} placeholder="1" />
                <TextFeld label="Anzahl der Bewohner" type="number" min={1} wert={e.anzahl_bewohner} onChange={set('anzahl_bewohner')} placeholder="1" />
              </FeldRaster>
              <Auswahl label="Mietintervall" wert={e.mietintervall || null} onChange={set('mietintervall')} optionen={optionen(MIETINTERVALLE, e.mietintervall)} />
              <div className="flex flex-col">
                <Schalter linie label="Erster Monat anteilig" an={e.miete_anteilig_beginn} onChange={set('miete_anteilig_beginn')}
                  hinweis="Die Miete für den ersten Monat wird anteilig berechnet, falls der Mietbeginn nicht auf den Monatsersten fällt." />
                <Schalter linie label="Letzter Monat anteilig" an={e.miete_anteilig_ende} onChange={set('miete_anteilig_ende')}
                  hinweis="Die Miete für den letzten Monat wird anteilig berechnet, falls das Vertragsende nicht auf das Monatsende fällt." />
                <Schalter linie label="Nur Werktage berücksichtigen" an={e.nur_werktage} onChange={set('nur_werktage')}
                  hinweis="Fällt der Fälligkeitstag auf ein Wochenende oder einen Feiertag, wird die Miete auf den nächsten Werktag verschoben." />
                <Schalter label="Sollstellung der Miete im Vormonat" an={e.sollstellung_vormonat} onChange={set('sollstellung_vormonat')}
                  hinweis="Die Sollstellung wird bereits im Vormonat erzeugt, z. B. die Januarmiete schon im Dezember." />
              </div>
              <Textbereich label="Notizen (optional)" wert={e.notizen} onChange={set('notizen')} platzhalter="Notizen zum Mietverhältnis …" />
            </InnenKarte>
            <InnenKarte>
              <KartenTitel icon={FileText} rechts={docs.length ? `${docs.length}` : undefined}>Mietvertrag & Dokumente</KartenTitel>
              {docs.map(d => (
                <div key={d.id} className="flex items-center flex-wrap" style={{ gap: 10, padding: '8px 0', borderBottom: '1px solid var(--line)' }}>
                  <FileText weight="fill" size={18} color="var(--acc)" />
                  <span className="flex flex-col flex-1 min-w-0" style={{ minWidth: 160 }}>
                    <span className="truncate" style={{ fontSize: 14, fontWeight: 700 }}>{d.datei_name || d.titel || 'Dokument'}</span>
                    <span style={{ fontSize: 12, color: 'var(--ink3)' }}>{d.kategorie || 'Ohne Kategorie'} · {deDatum(d.created_at)}</span>
                  </span>
                  <ImmoButton groesse={32} icon={Eye} aria-label="Anzeigen" title="Anzeigen" onClick={() => { void oeffneDok(d, false); }} />
                  <ImmoButton groesse={32} icon={DownloadSimple} aria-label="Herunterladen" title="Herunterladen" onClick={() => { void oeffneDok(d, true); }} />
                  <ImmoButton groesse={32} art="bad" icon={Trash} aria-label="Löschen" title="Löschen" onClick={() => setDokLoeschen(d)} />
                </div>
              ))}
              <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(min(220px,100%),1fr))', gap: 12 }}>
                <div role="button" tabIndex={0} onClick={() => datei.current?.click()} onKeyDown={x => { if (x.key === 'Enter' || x.key === ' ') { x.preventDefault(); datei.current?.click(); } }}
                  onDragOver={x => { x.preventDefault(); x.stopPropagation(); }}
                  onDrop={x => { x.preventDefault(); x.stopPropagation(); const f = x.dataTransfer.files?.[0]; if (f) void hochladen(f); }}
                  className="immo-hover-flaeche flex flex-col items-center text-center cursor-pointer"
                  style={{ borderRadius: 18, padding: 18, gap: 6, border: '2px dashed var(--line)', background: 'var(--fill)' }}>
                  <input ref={datei} type="file" className="hidden"
                    accept="application/pdf,image/jpeg,image/png,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.pdf,.doc,.docx,.jpg,.jpeg,.png"
                    onChange={x => { const f = x.target.files?.[0]; if (f) void hochladen(f); }} />
                  <UploadSimple size={22} color="var(--acc)" />
                  <span style={{ fontSize: 14, fontWeight: 800 }}>{laedtHoch ? 'Wird hochgeladen …' : 'Mietvertrag hochladen'}</span>
                  <span style={{ fontSize: 12, color: 'var(--ink3)' }}>oder hierher ziehen · max. 25 MB</span>
                </div>
                <div className="flex flex-col" style={{ gap: 8, borderRadius: 18, padding: 14, background: 'var(--fill)' }}>
                  <span className="flex items-center" style={{ gap: 6, fontSize: 14, fontWeight: 800 }}><LinkSimple size={16} color="var(--acc)" />Dokument verknüpfen</span>
                  <SearchSelect platzhalter="Bestehendes Dokument suchen …" suche={sucheDokument} onWahl={o => { void verknuepfen(o); }} />
                </div>
              </div>
            </InnenKarte>
          </>)}

          {reiter === 'miete' && (<>
            <InnenKarte>
              <KartenTitel icon={Wallet} rechts={
                <ImmoButton groesse={32} icon={Plus} disabled={!!standEntwurf}
                  onClick={() => setStandEntwurf({ istNeu: true, stand: neueAenderung(akt, e, Date.now()) })}>Mietänderung</ImmoButton>
              }>Mietkosten</KartenTitel>
              {standEntwurf && (
                <MietstandEditor stand={standEntwurf.stand} istNeu={standEntwurf.istNeu} andereStaende={staende.filter(z => z.id !== standEntwurf.stand.id)}
                  onAbbrechen={() => setStandEntwurf(null)}
                  onUebernehmen={neu => { void sichereStaende([...staende.filter(z => z.id !== neu.id), neu], standEntwurf.istNeu ? 'Mietänderung gespeichert' : 'Mietstand gespeichert').then(ok => { if (ok) setStandEntwurf(null); }); }} />
              )}
              {staende.length === 0 && <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Noch kein Mietstand erfasst.</span>}
              <div className="flex flex-col">
                {staende.map((z, i) => {
                  const aktuell = akt?.id === z.id;
                  const geplant = !!z.gueltigAb && z.gueltigAb > heute;
                  return (
                    <div key={z.id} className="flex items-center flex-wrap" style={{ gap: 10, padding: '10px 0', borderBottom: '1px solid var(--line)' }}>
                      <span className="flex flex-col flex-1 min-w-0" style={{ minWidth: 180, gap: 2 }}>
                        <span className="flex items-center flex-wrap" style={{ gap: 6, fontSize: 14, fontWeight: 800 }}>
                          {zeitraumLabel(staende, i)}
                          {aktuell && <Chip groesse="xs" ton="acc">aktuell</Chip>}
                          {geplant && <Chip groesse="xs" ton="neutral">geplant</Chip>}
                        </span>
                        <span className="immo-zahl" style={{ fontSize: 12, color: 'var(--ink3)' }}>
                          Kalt {eur(z.kaltmiete)} · BK {eur(z.betriebskosten)} · HK {eur(z.heizkosten)} · Weitere {eur(z.weitereKosten)}
                          {(z.positionen ?? []).length > 0 ? ` (${(z.positionen ?? []).map(positionLabel).join(', ')})` : ''}
                        </span>
                      </span>
                      <span className="immo-zahl" style={{ fontSize: 15, fontWeight: 800 }}>{eur(gesamtmiete(z))}</span>
                      <ImmoButton groesse={32} icon={PencilSimple} aria-label="Mietstand bearbeiten" title="Mietstand bearbeiten" disabled={!!standEntwurf}
                        onClick={() => setStandEntwurf({ stand: z, istNeu: false })} />
                      {staende.length > 1 && <ImmoButton groesse={32} art="bad" icon={Trash} aria-label="Mietstand löschen" title="Mietstand löschen" onClick={() => setStandLoeschen(z)} />}
                    </div>
                  );
                })}
              </div>
              <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Mietänderungen und Mietstände werden sofort gespeichert; die bisherige Miete bleibt erhalten.</span>
            </InnenKarte>

            <InnenKarte>
              <KartenTitel rechts={akt?.gueltigAb ? `ab ${deDatum(akt.gueltigAb)}` : undefined}>Aktueller Stand – zum Korrigieren</KartenTitel>
              <FeldRaster minBreite={150}>
                <TextFeld label="Kaltmiete" wert={e.kaltmiete} onChange={set('kaltmiete')} inputMode="decimal" suffix="€" />
                <TextFeld label="Betriebskosten" wert={e.nebenkosten_vorauszahlung} onChange={set('nebenkosten_vorauszahlung')} inputMode="decimal" suffix="€" />
                <TextFeld label="Heizkosten" wert={e.heizkosten} onChange={set('heizkosten')} inputMode="decimal" suffix="€" />
                {aktPositionen.length > 0
                  ? <TextFeld label="Weitere Kosten" wert={String(summePositionen(aktPositionen)).replace('.', ',')} onChange={() => undefined} readOnly suffix="€" title="Summe der Positionen – über „Mietstand bearbeiten“ ändern" />
                  : <TextFeld label="Weitere Kosten" wert={e.weitere_kosten} onChange={set('weitere_kosten')} inputMode="decimal" suffix="€" />}
              </FeldRaster>
              {aktPositionen.length > 0 && (
                <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Weitere Kosten ergeben sich aus den Positionen ({aktPositionen.map(positionLabel).join(', ')}) – über „Mietstand bearbeiten“ ändern.</span>
              )}
              <Schalter label="zzgl. MwSt." an={e.mwst} onChange={set('mwst')} hinweis="Bitte nur auswählen, wenn dies mit dem Mieter vereinbart wurde." />
              <div className="flex items-center justify-between" style={{ gap: 10 }}>
                <span style={{ fontSize: 14, fontWeight: 800 }}>Gesamtmiete</span>
                <span className="immo-zahl" style={{ fontSize: 20, fontWeight: 800 }}>{eur(gesamt)}</span>
              </div>
            </InnenKarte>

            <InnenKarte>
              <KartenTitel>Kaution</KartenTitel>
              <Schalter label="Sollstellung für Kaution generieren" an={kautionAn} onChange={setKautionAn} />
              {kautionAn && (<>
                <Auswahl label="Kautionsart" wert={e.kaution_art || null} onChange={set('kaution_art')} optionen={optionen(KAUTIONSARTEN, e.kaution_art)} />
                <FeldRaster minBreite={170}>
                  <TextFeld label="Fälligkeit" type="date" wert={e.kaution_faelligkeit} onChange={set('kaution_faelligkeit')} />
                  <TextFeld label="Kautionshöhe" wert={e.kaution} onChange={set('kaution')} inputMode="decimal" suffix="€" />
                </FeldRaster>
              </>)}
            </InnenKarte>

            <InnenKarte>
              <KartenTitel>Anfangssaldo</KartenTitel>
              <Schalter label="Sollstellung für offenen Mietbetrag generieren" an={e.anfangssaldo_aktiv} onChange={set('anfangssaldo_aktiv')} />
              {e.anfangssaldo_aktiv && (
                <FeldRaster minBreite={170}>
                  <TextFeld label="Fälligkeit" type="date" wert={e.anfangssaldo_faelligkeit} onChange={set('anfangssaldo_faelligkeit')} />
                  <TextFeld label="Mietbetrag" wert={e.anfangssaldo_betrag} onChange={set('anfangssaldo_betrag')} inputMode="decimal" suffix="€" />
                </FeldRaster>
              )}
            </InnenKarte>

            <InnenKarte>
              <KartenTitel>SEPA-Lastschrifteinzug</KartenTitel>
              <Schalter label="SEPA-Lastschrifteinzug aktivieren" an={e.sepa_aktiv} onChange={set('sepa_aktiv')} />
              {e.sepa_aktiv && (
                <FeldRaster minBreite={170}>
                  <TextFeld label="Mandatsreferenz" wert={e.sepa_mandatsreferenz} onChange={set('sepa_mandatsreferenz')} placeholder="z. B. T009000100000080053650O" />
                  <TextFeld label="Mandatsdatum" type="date" wert={e.sepa_mandatsdatum} onChange={set('sepa_mandatsdatum')} />
                </FeldRaster>
              )}
            </InnenKarte>
          </>)}

          {reiter === 'einzug' && (<>
            {(['einzug', 'auszug'] as const).map(art => (
              <InnenKarte key={art}>
                <KartenTitel icon={ClipboardText}>{art === 'einzug' ? 'Einzug' : 'Auszug'}</KartenTitel>
                <FeldRaster minBreite={170}>
                  <TextFeld label="Zählerstand Gas" wert={e[`${art}_zaehler_gas`]} onChange={set(`${art}_zaehler_gas`)} inputMode="decimal" />
                  <TextFeld label="Zählerstand Strom" wert={e[`${art}_zaehler_strom`]} onChange={set(`${art}_zaehler_strom`)} inputMode="decimal" />
                </FeldRaster>
                <Textbereich label="Notizen (optional)" wert={e[`${art}_notizen`]} onChange={set(`${art}_notizen`)} platzhalter="Zusätzliche Notizen zum Mieter" />
                <div className="flex items-center justify-between flex-wrap" style={{ gap: 10 }}>
                  <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Wasser- und Wärmezähler sowie das vollständige Protokoll (Räume, Schlüssel, Unterschriften) im Übergabeprotokoll.</span>
                  <ImmoButton groesse={36} icon={ClipboardText} onClick={() => navigate('/admin/uebergabeprotokolle')}>Übergabeprotokolle</ImmoButton>
                </div>
              </InnenKarte>
            ))}
          </>)}
        </div>

        <div className="flex flex-col" style={{ flex: '1 1 250px', maxWidth: 320, minWidth: 0, gap: 12, position: 'sticky', top: 16 }}>
          <LivePreviewCard>
            <div className="flex items-center min-w-0" style={{ gap: 12 }}>
              <span className="flex items-center justify-center shrink-0" style={{ width: 46, height: 46, borderRadius: '50%', background: 'var(--acc-fill)', color: 'var(--acc-ink)', fontSize: 15, fontWeight: 600 }}>{initialen(name)}</span>
              <div className="flex flex-col min-w-0">
                <span className="truncate" style={{ fontSize: 15, fontWeight: 800 }}>{name}{mitmieter.length ? ` +${mitmieter.length}` : ''}</span>
                <span className="truncate" style={{ fontSize: 12, color: 'var(--ink3)' }}>{s.bezeichnung} · {e.vertragsart || 'Mietverhältnis'}</span>
              </div>
            </div>
            <div className="flex items-end justify-between" style={{ gap: 8 }}>
              <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Gesamtmiete{e.mwst ? ' zzgl. MwSt.' : ''}</span>
              <span className="immo-zahl" style={{ fontSize: 24, fontWeight: 800 }}>{eur(gesamt)}</span>
            </div>
            <div className="flex flex-col" style={{ gap: 6, paddingTop: 12, borderTop: '1px solid var(--line)', fontSize: 12, color: 'var(--ink2)' }}>
              <span className="flex items-center" style={{ gap: 8 }}><CalendarBlank size={14} />{e.mietbeginn ? `ab ${deDatum(e.mietbeginn)}` : 'Mietbeginn fehlt'}{e.mietende ? ` bis ${deDatum(e.mietende)}` : ''}</span>
              <span className="flex items-center" style={{ gap: 8 }}><Wallet size={14} />Kaution {kautionAn && leseZahl(e.kaution) ? eur(leseZahl(e.kaution) ?? 0) : '–'}{kautionAn && e.kaution_art ? ` · ${e.kaution_art}` : ''}</span>
              <span className="flex items-center" style={{ gap: 8 }}><Handshake size={14} />{e.sepa_aktiv ? 'SEPA-Lastschrift' : 'Überweisung'}{e.miete_faellig_tag ? ` · fällig am ${e.miete_faellig_tag}.` : ''}</span>
            </div>
          </LivePreviewCard>
          <div className="flex flex-col" style={{ gap: 8 }}>
            <ImmoButton art="akzent" icon={FloppyDisk} disabled={speichert || !geaendert} onClick={() => { void speichere(); }}>
              {speichert ? 'Speichert …' : 'Speichern'}
            </ImmoButton>
            <ImmoButton onClick={abbrechen}>{geaendert ? 'Abbrechen' : 'Schließen'}</ImmoButton>
            <span className="text-center" style={{ fontSize: 11, color: geaendert ? 'var(--warn)' : 'var(--ink3)' }}>
              {geaendert ? 'Ungespeicherte Änderungen' : 'Keine ungespeicherten Änderungen'}
            </span>
          </div>
        </div>
      </div>

      {standLoeschen && (
        <BestaetigenDialog titel="Mietstand löschen?" text="Der Zeitraum davor gilt dann bis zum nächsten Stand weiter."
          detail={`${standLoeschen.gueltigAb ? `ab ${deDatum(standLoeschen.gueltigAb)}` : 'ohne Datum'} · ${eur(gesamtmiete(standLoeschen))}`}
          bestaetigenLabel="Löschen"
          onBestaetigen={() => { const weg = standLoeschen; setStandLoeschen(null); void sichereStaende(staende.filter(z => z.id !== weg.id), 'Mietstand gelöscht'); }}
          onAbbrechen={() => setStandLoeschen(null)} />
      )}
      {dokLoeschen && (
        <BestaetigenDialog titel="Dokument löschen?" text="Die Datei wird endgültig gelöscht." detail={dokLoeschen.datei_name || dokLoeschen.titel || 'Dokument'}
          bestaetigenLabel="Löschen" onBestaetigen={() => { void loescheDok(); }} onAbbrechen={() => setDokLoeschen(null)} />
      )}
      {verlassen && (
        <BestaetigenDialog titel="Änderungen verwerfen?" text="Die ungespeicherten Änderungen gehen verloren. Bereits gespeicherte Mietstände und Dokumente bleiben erhalten."
          bestaetigenLabel="Verwerfen" icon={Trash} onBestaetigen={() => { setVerlassen(false); onFertig(); }} onAbbrechen={() => setVerlassen(false)} />
      )}
    </>
  );
}
